import React, { useEffect, useRef } from 'react';
import { fmt } from '../lib/formato';

const fmtD = s => s ? new Date(s + 'T00:00:00').toLocaleDateString('pt-BR') : '';

// Comissão paga (roadmap #6): um toggle por parceiro, com a data em que foi marcada
function PagoBtn({ p, onClick }) {
  const pago = !!p.paid_at;
  return <button onClick={onClick}
    title={pago ? `Comissão paga em ${fmtD(p.paid_at)}. Clique para desmarcar` : 'Comissão a pagar. Clique para marcar como paga'}
    className={`flex items-center gap-1 mx-auto text-xs font-semibold px-2.5 py-1 rounded-full transition-colors whitespace-nowrap ${pago ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>
    {pago ? `✓ Pago ${fmtD(p.paid_at).slice(0, 5)}` : '○ A pagar'}
  </button>;
}

function DualScrollTable({ filtered, tipo, recMonths, recLabel, onToggle, onTogglePaid, totalMensal, totalRepasse }) {
  const topRef       = React.useRef(null);
  const botRef       = React.useRef(null);
  const tableRef     = React.useRef(null);
  const spacerTopRef = React.useRef(null);
  const spacerBotRef = React.useRef(null);

  React.useEffect(() => {
    const top = topRef.current;
    const bot = botRef.current;
    const tbl = tableRef.current;
    if (!top || !bot || !tbl) return;

    const updateWidth = () => {
      const w = tbl.scrollWidth + 'px';
      if (spacerTopRef.current) spacerTopRef.current.style.width = w;
      if (spacerBotRef.current) spacerBotRef.current.style.width = w;
    };
    updateWidth();

    let busy = false;
    const onTop = () => { if (!busy) { busy=true; tbl.scrollLeft=top.scrollLeft; bot.scrollLeft=top.scrollLeft; busy=false; } };
    const onBot = () => { if (!busy) { busy=true; tbl.scrollLeft=bot.scrollLeft; top.scrollLeft=bot.scrollLeft; busy=false; } };
    const onTbl = () => { if (!busy) { busy=true; top.scrollLeft=tbl.scrollLeft; bot.scrollLeft=tbl.scrollLeft; busy=false; } };
    top.addEventListener('scroll', onTop);
    bot.addEventListener('scroll', onBot);
    tbl.addEventListener('scroll', onTbl);

    const ro = new ResizeObserver(updateWidth);
    ro.observe(tbl);
    return () => {
      top.removeEventListener('scroll', onTop);
      bot.removeEventListener('scroll', onBot);
      tbl.removeEventListener('scroll', onTbl);
      ro.disconnect();
    };
  }, [filtered]);

  const scroll = (dir) => { if (tableRef.current) tableRef.current.scrollLeft += dir * 300; };

  const Btn = ({ dir }) => <button
    onClick={() => scroll(dir)}
    title={dir === -1 ? 'Rolar para esquerda' : 'Rolar para direita'}
    style={{userSelect:'none'}}
    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-white border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 text-gray-500 shadow transition-all font-bold text-2xl leading-none cursor-pointer select-none">
    {dir === -1 ? '‹' : '›'}
  </button>;

  const ScrollStrip = ({ stripRef, spacerRef: sRef }) => <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-gray-100">
    <Btn dir={-1} />
    <div ref={stripRef} className="flex-1 scroll-table" style={{overflowX:'scroll',overflowY:'hidden',height:'22px'}}>
      <div ref={sRef} style={{height:'1px',minWidth:'100%'}}></div>
    </div>
    <Btn dir={1} />
  </div>;

  return <div>
    {/* ── Barra de rolagem SUPERIOR ── */}
    <ScrollStrip stripRef={topRef} spacerRef={spacerTopRef} />

    {/* Tabela (scrollbar nativo oculto — controlado pelas strips) */}
    <div ref={tableRef} className="scroll-table-hidden">
      <table className="w-full text-sm whitespace-nowrap">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 sticky left-0 bg-white z-10 shadow-[2px_0_4px_rgba(0,0,0,0.06)]">Empresa</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400">Valor/mês</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Tipo</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Duração</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Parceiro 1</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">%</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400">Pagar P1</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Pago P1</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Parceiro 2</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">%</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400">Pagar P2</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Pago P2</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400">Total Repasse</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Cobrança</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Contrato</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Enviado</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Assinado</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400">Data Assinatura</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((l, i) => {
            const p1  = l.partners?.[0] || null;
            const p2  = l.partners?.[1] || null;
            const v   = Number(l.value)||0;
            const p1v = p1 ? v*(Number(p1.commission_rate)||0)/100 : 0;
            const p2v = p2 ? v*(Number(p2.commission_rate)||0)/100 : 0;
            const tot = p1v + p2v;
            return <tr key={l.id} className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors ${i%2===0?'':'bg-gray-50/40'}`}>
              <td className={`px-4 py-3 font-semibold text-gray-800 sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.05)] ${i%2===0?'bg-white':'bg-gray-50/80'}`}>{l.company_name}</td>
              <td className="px-4 py-3 text-right font-bold text-green-600">{fmt(v)}</td>
              <td className="px-4 py-3 text-center">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tipo(l.recurrence)==='Contínuo' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                  {tipo(l.recurrence)}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-500 text-xs">
                {l.recurrence && l.recurrence!=='unica' ? `${recMonths(l.recurrence)}x · ${recLabel(l.recurrence)}` : 'Único'}
              </td>
              <td className="px-4 py-3 text-gray-700">{p1 ? p1.name : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-center text-gray-500">{p1 ? `${Number(p1.commission_rate)||0}%` : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-right font-semibold text-purple-600">{p1 ? fmt(p1v) : <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-3 text-center" onClick={e=>e.stopPropagation()}>{p1 && onTogglePaid ? <PagoBtn p={p1} onClick={()=>onTogglePaid(l.id, p1.id)} /> : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-gray-700">{p2 ? p2.name : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-center text-gray-500">{p2 ? `${Number(p2.commission_rate)||0}%` : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-right font-semibold text-purple-600">{p2 ? fmt(p2v) : <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-3 text-center" onClick={e=>e.stopPropagation()}>{p2 && onTogglePaid ? <PagoBtn p={p2} onClick={()=>onTogglePaid(l.id, p2.id)} /> : <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-3 text-right font-bold text-red-500">{tot > 0 ? fmt(tot) : <span className="text-gray-300">—</span>}</td>
              {/* Toggle: Cobrança ativa */}
              <td className="px-4 py-3 text-center" onClick={e=>e.stopPropagation()}>
                <button onClick={()=>onToggle(l.id,'billing_active', !(l.billing_active!==false))}
                  title={l.billing_active===false ? 'Cobrança pausada — clique para ativar' : 'Cobrança ativa — clique para pausar'}
                  className={`flex items-center gap-1 mx-auto text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${l.billing_active===false ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                  {l.billing_active===false ? '⏸ Pausada' : '✅ Ativa'}
                </button>
              </td>
              {/* Toggle: Contrato pausado */}
              <td className="px-4 py-3 text-center" onClick={e=>e.stopPropagation()}>
                <button onClick={()=>onToggle(l.id,'contract_paused', !l.contract_paused)}
                  title={l.contract_paused ? 'Contrato pausado — clique para reativar' : 'Contrato ativo — clique para pausar'}
                  className={`flex items-center gap-1 mx-auto text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${l.contract_paused ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                  {l.contract_paused ? '⏸ Pausado' : '✅ Ativo'}
                </button>
              </td>
              {/* Toggle: Contrato enviado */}
              <td className="px-4 py-3 text-center" onClick={e=>e.stopPropagation()}>
                <button onClick={()=>onToggle(l.id,'contract_sent', !l.contract_sent)}
                  title={l.contract_sent ? 'Contrato enviado — clique para desmarcar' : 'Contrato não enviado — clique para marcar'}
                  className={`flex items-center gap-1 mx-auto text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${l.contract_sent ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                  {l.contract_sent ? '📤 Enviado' : '— Não enviado'}
                </button>
              </td>
              {/* Toggle: Contrato assinado */}
              <td className="px-4 py-3 text-center" onClick={e=>e.stopPropagation()}>
                <button onClick={()=>onToggle(l.id,'contract_signed', !l.contract_signed)}
                  title={l.contract_signed ? 'Contrato assinado — clique para desmarcar' : 'Contrato não assinado — clique para marcar'}
                  className={`flex items-center gap-1 mx-auto text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${l.contract_signed ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                  {l.contract_signed ? '✍️ Assinado' : '— Não assinado'}
                </button>
              </td>
              {/* Data de assinatura */}
              <td className="px-3 py-2 text-center" onClick={e=>e.stopPropagation()}>
                <input
                  type="date"
                  value={l.contract_signed_date||''}
                  onChange={e=>onToggle(l.id,'contract_signed_date', e.target.value||null)}
                  title="Data da assinatura do contrato"
                  className={`apple-input text-xs px-2 py-1 w-36 text-center cursor-pointer ${l.contract_signed_date ? 'text-emerald-700 font-semibold' : 'text-gray-400'}`}
                />
              </td>
            </tr>;
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-900">
            <td className="px-4 py-3 text-xs font-bold text-white sticky left-0 bg-gray-900 z-10">TOTAL ({filtered.length})</td>
            <td className="px-4 py-3 text-right font-bold text-green-400">{fmt(totalMensal)}</td>
            <td colSpan={10}></td>
            <td className="px-4 py-3 text-right font-bold text-red-400">{fmt(totalRepasse)}</td>
            <td colSpan={5}></td>
          </tr>
        </tfoot>
      </table>
    </div>

    {/* ── Barra de rolagem INFERIOR ── */}
    <ScrollStrip stripRef={botRef} spacerRef={spacerBotRef} />
  </div>;
}

// ─── LostLeadsView ─────────────────────────────────────────

export { DualScrollTable };
